import { useMutation, useQuery, useQueryClient } from "react-query";
import {
    GetMaintenanceSummary,
    GetMaintenanceReports,
    GetMaintenanceWorkOrders,
    AddMaintenanceWorkOrder,
    GetMaintenanceWorkOrderById,
    UpdateMaintenanceWorkOrder,
    UpdateMaintenanceWorkOrderStatus,
    UpdateMaintenanceWorkOrderCost,
    DeleteMaintenanceWorkOrder,
    GetMaintenanceInteractions,
    AddMaintenanceInteraction,
} from "../services/backend";
import { Notify, NotifyError } from "../utils/functions";

// refetchOnMount: 'always' on these three (same pattern as use-users.js's
// useGetUsers) — the board is reached by navigating back from the detail page,
// which unmounts and remounts this query's only consumer. Invalidating the cache
// from the detail page's mutation should be enough on its own, but a status
// change made there was observed to still show the old column until a manual
// refresh; forcing a refetch on every mount removes the dependency on that
// invalidation-then-remount timing working out.
export function useGetMaintenanceSummary() {
    return useQuery(['maintenance-summary'], () => GetMaintenanceSummary(), {
        refetchOnWindowFocus: false,
        refetchOnMount: 'always',
        staleTime: 60 * 1000,
        select: (data) => data?.data?.summary || {},
        onError: (error) => {
            NotifyError("Failed to fetch maintenance summary", error);
            console.error("Error fetching maintenance summary:", error);
        }
    });
}

export function useGetMaintenanceReports() {
    return useQuery(['maintenance-reports'], () => GetMaintenanceReports(), {
        refetchOnWindowFocus: false,
        refetchOnMount: 'always',
        staleTime: 60 * 1000,
        select: (data) => data?.data || {},
        onError: (error) => {
            NotifyError("Failed to fetch maintenance reports", error);
            console.error("Error fetching maintenance reports:", error);
        }
    });
}

export function useGetWorkOrders(filters = {}) {
    return useQuery(['maintenance-work-orders', filters], () => GetMaintenanceWorkOrders(filters), {
        refetchOnWindowFocus: false,
        refetchOnMount: 'always',
        staleTime: 30 * 1000,
        select: (data) => data?.data?.workOrders || [],
        onError: (error) => {
            NotifyError("Failed to fetch work orders", error);
            console.error("Error fetching work orders:", error);
        }
    });
}

export function useGetWorkOrderById(workOrderId) {
    return useQuery(['maintenance-work-order', workOrderId], () => GetMaintenanceWorkOrderById(workOrderId), {
        enabled: !!workOrderId,
        refetchOnWindowFocus: false,
        staleTime: 0,
        select: (data) => data?.data?.workOrder || null,
        onError: (error) => {
            NotifyError("Failed to fetch work order details", error);
            console.error("Error fetching work order details:", error);
        }
    });
}

export function useAddWorkOrder() {
    const queryClient = useQueryClient();
    return useMutation(AddMaintenanceWorkOrder, {
        onSuccess: () => {
            Notify("Work order created successfully", "success");
            queryClient.invalidateQueries('maintenance-work-orders');
            queryClient.invalidateQueries('maintenance-summary');
        },
        onError: (error) => {
            NotifyError("Failed to create work order", error);
            console.error("Error creating work order:", error);
        }
    });
}

export function useUpdateWorkOrder() {
    const queryClient = useQueryClient();
    return useMutation(({ workOrderId, data }) => UpdateMaintenanceWorkOrder(workOrderId, data), {
        onSuccess: (_, variables) => {
            Notify("Work order updated successfully", "success");
            queryClient.invalidateQueries('maintenance-work-orders');
            queryClient.invalidateQueries('maintenance-summary');
            if (variables?.workOrderId) queryClient.invalidateQueries(['maintenance-work-order', variables.workOrderId]);
        },
        onError: (error) => {
            NotifyError("Failed to update work order", error);
            console.error("Error updating work order:", error);
        }
    });
}

// No success toast — this mutation fires on every drag-and-drop card move, which is
// already visually obvious, so a toast per drop would just be noise.
export function useUpdateWorkOrderStatus() {
    const queryClient = useQueryClient();
    return useMutation(({ workOrderId, data }) => UpdateMaintenanceWorkOrderStatus(workOrderId, data), {
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries('maintenance-work-orders');
            queryClient.invalidateQueries('maintenance-summary');
            queryClient.invalidateQueries('maintenance-reports');
            if (variables?.workOrderId) queryClient.invalidateQueries(['maintenance-work-order', variables.workOrderId]);
        },
        onError: (error) => {
            NotifyError("Failed to move work order", error);
            console.error("Error updating work order status:", error);
            queryClient.invalidateQueries('maintenance-work-orders');
        }
    });
}

export function useUpdateWorkOrderCost() {
    const queryClient = useQueryClient();
    return useMutation(({ workOrderId, data }) => UpdateMaintenanceWorkOrderCost(workOrderId, data), {
        onSuccess: (_, variables) => {
            Notify("Actual cost updated successfully", "success");
            queryClient.invalidateQueries('maintenance-work-orders');
            queryClient.invalidateQueries('maintenance-summary');
            queryClient.invalidateQueries('maintenance-reports');
            if (variables?.workOrderId) queryClient.invalidateQueries(['maintenance-work-order', variables.workOrderId]);
            if (variables?.workOrderId) queryClient.invalidateQueries(['maintenance-interactions', variables.workOrderId]);
        },
        onError: (error) => {
            NotifyError("Failed to update actual cost", error);
            console.error("Error updating actual cost:", error);
        }
    });
}

export function useDeleteWorkOrder() {
    const queryClient = useQueryClient();
    return useMutation(DeleteMaintenanceWorkOrder, {
        onSuccess: () => {
            Notify("Work order deleted successfully", "success");
            queryClient.invalidateQueries('maintenance-work-orders');
            queryClient.invalidateQueries('maintenance-summary');
        },
        onError: (error) => {
            NotifyError("Failed to delete work order", error);
            console.error("Error deleting work order:", error);
        }
    });
}

export function useGetWorkOrderInteractions(workOrderId) {
    return useQuery(['maintenance-interactions', workOrderId], () => GetMaintenanceInteractions(workOrderId), {
        enabled: !!workOrderId,
        refetchOnWindowFocus: false,
        staleTime: 10 * 1000,
        select: (data) => data?.data?.interactions || [],
        onError: (error) => {
            NotifyError("Failed to fetch comments", error);
            console.error("Error fetching work order interactions:", error);
        }
    });
}

export function useAddWorkOrderInteraction() {
    const queryClient = useQueryClient();
    return useMutation(({ workOrderId, data }) => AddMaintenanceInteraction(workOrderId, data), {
        onSuccess: (_, variables) => {
            if (variables?.workOrderId) queryClient.invalidateQueries(['maintenance-interactions', variables.workOrderId]);
        },
        onError: (error) => {
            NotifyError("Failed to add comment", error);
            console.error("Error adding work order interaction:", error);
        }
    });
}
